import { AddressDetails } from '../../shared/models/address.types';
import { ContactDetails } from '../../shared/models/contact.types';

export type AgentType = 'field' | 'agency';
export type AgentStatus = 'active' | 'inactive';
export type AgentApprovalStatus = 'pending';
export type TaskPriority = 'high' | 'medium' | 'low';
export type TaskStatus = 'pending' | 'in-progress' | 'done' | 'overdue';

export interface AgentAgencyAssignment {
  id: string;
  name: string;
  city?: string;
  area?: string;
}

export interface PosAssignment {
  id: string;
  name: string;
  city?: string;
  area?: string;
  agencyId?: string;
  agencyName?: string;
}

export interface AgentModel {
  id: string;
  name: string;
  email: string;
  contact: ContactDetails;
  phoneDisplay: string;
  address: AddressDetails;
  city: string;
  area: string;
  assignedAgencies: AgentAgencyAssignment[];
  assignedPos: PosAssignment[];
  agencyId: string;
  agencyName: string;
  posAssigned: number;
  type: AgentType;
  status: AgentStatus;
  dealerId: string;
  joinedAt: string;
}

export interface AgentApprovalRequestModel {
  id: string;
  name: string;
  email: string;
  contact: ContactDetails;
  address: AddressDetails;
  requestedAgency: AgentAgencyAssignment;
  requestedPos: PosAssignment[];
  requestDate: string;
  approvalStatus: AgentApprovalStatus;
  type: AgentType;
  dealerId: string;
}

export interface AgentUpsertPayload {
  name: string;
  type: AgentType;
  status: AgentStatus;
  dealerId: string;
  joinedAt: string;
  contact: ContactDetails;
  address: AddressDetails;
  assignedAgencies: AgentAgencyAssignment[];
  assignedPos: PosAssignment[];
}

export interface TaskModel {
  id: string;
  title: string;
  description: string;
  agentId: string;
  agentName: string;
  agencyId: string;
  agencyName: string;
  posId: string;
  posName: string;
  dueDate: string;
  priority: TaskPriority;
  status: TaskStatus;
  createdAt: string;
  assetAssignments?: TaskAssetAssignment[];
}

export interface TaskAssetAssignment {
  assetId: string;
  assetName: string;
  quantity: number;
  sourceAgencyId: string;
  sourceAgencyName: string;
  remarks?: string;
}

export interface AgentAssignedAssetRecord {
  id: string;
  agentId: string;
  assignedAt: string;
  assetId: string;
  assetName: string;
  quantity: number;
  sourceAgencyId: string;
  sourceAgencyName: string;
  assignedBy: string;
  relatedTaskId?: string;
  relatedTaskTitle?: string;
  status: 'assigned' | 'partially-used' | 'returned';
  remarks?: string;
}

export interface AgentRecord extends AgentModel {}

export interface AgentApprovalRequestRecord extends AgentApprovalRequestModel {}

export interface TaskRecord extends TaskModel {}

export function toAgentRecord(id: string, payload: AgentUpsertPayload, current?: AgentRecord): AgentRecord {
  const primaryAgency = payload.assignedAgencies[0];

  return {
    id,
    name: payload.name,
    email: payload.contact.email,
    contact: payload.contact,
    phoneDisplay: formatAgentPhoneDisplay(payload.contact),
    address: payload.address,
    city: payload.address.locality,
    area: payload.address.area,
    assignedAgencies: payload.assignedAgencies,
    assignedPos: payload.assignedPos,
    agencyId: primaryAgency?.id ?? '',
    agencyName: primaryAgency?.name ?? 'Unassigned',
    posAssigned: payload.assignedPos.length,
    type: payload.type,
    status: payload.status,
    dealerId: payload.dealerId,
    joinedAt: payload.joinedAt,
    ...(current ? { id: current.id } : {})
  };
}

export function formatAgentPhoneDisplay(contact: ContactDetails): string {
  const dialCode = contact.phone.dialCode ? `+${contact.phone.dialCode}` : '';
  return [dialCode, contact.phone.number].filter(Boolean).join(' ').trim();
}
